// The page document names widgets by type and profile keys (device-profile.json `widget`); the widget registry
// takes dashboard widgets with bindings. This turns one into the other for one device, and builds the widget input
// from the page and the latest values (widget-fixtures WFX-02).
import type { Binding as DocBinding } from "../../../documents/generated/dashboard";
import type { DeviceState, LatestValue, PointState, WidgetInput } from "../../../widgets/generated/widget-fixture";
import type { Band } from "../../../widgets/expected";
import type { Widget } from "../../../widgets/types";
import type { Binding, LatestResult, WidgetPage } from "./queries";

type ProfileWidget = { [k: string]: unknown };

const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

/**
 * A compass's role comes from the point, since a profile lists the keys only: the reading in degrees is the
 * direction, a key naming a gust is the gust, anything else the speed.
 */
function compassRole(key: string, page: WidgetPage): DocBinding["role"] {
  if (page.points[key]?.unit === "°") return "direction";
  return /gust/.test(key) ? "gust" : "speed";
}

/**
 * A profile widget carries its type's own keys (gauge bands, slider limits and the like) beside its keys; on a
 * dashboard widget they live in `config`.
 */
export function toWidget(raw: ProfileWidget, index: number, device: string, page: WidgetPage): Widget {
  const { type: rawType, datapoints: d, commands: c, settings: s, title, config, ...own } = raw;
  const type = typeof rawType === "string" ? rawType : "unknown";
  const datapoints = strings(d);
  const commands = strings(c);
  const settings = strings(s);
  const nested = config !== null && typeof config === "object" && !Array.isArray(config) ? config : {};
  const bindings: DocBinding[] = [
    ...commands.map((k) => (page.actions[k]?.kind === "setting" ? { device, setting: k } : { device, command: k })),
    ...settings.filter((k) => !commands.includes(k)).map((k) => ({ device, setting: k })),
    ...datapoints.map((k) => ({ device, datapoint: k, ...(type === "compass" ? { role: compassRole(k, page) } : {}) })),
  ];
  return {
    id: `page-${index}`,
    type,
    ...(typeof title === "string" ? { title } : {}),
    position: { x: 0, y: index, w: 6, h: 2 },
    bindings,
    config: { ...own, ...nested },
  };
}

/** The most values a home tile shows (device-type-pages DTP-11). */
const TILE_VALUES = 4;

/**
 * The device's small home tile, for the map's device sheet (maps-2 MAP2-22): the type's tile as it is now, which the page
 * carries since vision-api 0.75.0 (the console's, else `ui.home`, else the card values); from an older server, one made
 * from the card's primary and secondary values, as the API makes a type's tile when the type has none (DTP-10).
 */
export function sheetTile(device: string, page: WidgetPage): Widget {
  const base = { id: `tile-${device}`, title: page.device.name, position: { x: 0, y: 0, w: 3, h: 2 } };
  if (page.home_tile) return { ...base, ...(page.home_tile as Omit<Widget, "id" | "position">) };
  return {
    ...base,
    type: "device_tile",
    bindings: cardKeys(page)
      .slice(0, TILE_VALUES)
      .map((datapoint) => ({ device, datapoint })),
    config: {},
  };
}

/** The datapoints a widget reads, for the sheet's POST /v1/latest. */
export function widgetBindings(widget: Widget): Binding[] {
  return (widget.bindings ?? []).flatMap((b) => (b.datapoint ? [{ device: b.device, datapoint: b.datapoint }] : []));
}

function cardKeys(page: WidgetPage): string[] {
  const card = page.card as { primary?: string; secondary?: string[] } | undefined;
  return [card?.primary, ...(card?.secondary ?? [])].filter((k): k is string => !!k && k in page.points);
}

/** The datapoints the page's widgets and card read, for one POST /v1/latest. */
export function pageBindings(device: string, page: WidgetPage): Binding[] {
  return Object.keys(page.points).map((datapoint) => ({ device, datapoint }));
}

/**
 * The widget input for one device. `bands` are the page document's normal bands (DDB-01); they ride on the point
 * as `expected` until widget-fixture 0.2.0 gives PointState the field (DDB-05).
 */
export function widgetInput(
  device: string,
  page: WidgetPage,
  latest: LatestResult | undefined,
  bands: Record<string, Band> = {},
): WidgetInput {
  const points: Record<string, PointState> = {};
  for (const [k, info] of Object.entries(page.points)) {
    const v = latest?.points[`${device}/${k}`];
    const band = bands[k];
    points[`${device}/${k}`] = {
      ...info,
      ...(band ? { expected: [band[0], band[1]] } : {}),
      latest: v ? { ...v, value: v.value as LatestValue["value"] } : null,
    };
  }
  const actions: WidgetInput["actions"] = {};
  for (const [k, info] of Object.entries(page.actions)) actions[`${device}/${k}`] = { ...info, state: "idle" };
  return { devices: { [device]: deviceState(latest?.devices[device] ?? page.device) }, points, actions };
}

/** A position without both lat and lon is no position (vision-api 0.13.1 requires both; older servers may not). */
function deviceState(d: WidgetPage["device"]): DeviceState {
  const p = d.position;
  const position =
    p && typeof p.lat === "number" && typeof p.lon === "number" ? { ...p, lat: p.lat, lon: p.lon } : null;
  return { ...d, position };
}
